import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../utils/api';
import useLocalStorage from '../hooks/useLocalStorage';
import { KEYS } from '../utils/storage';
import { useToast } from '../components/common/Toast';
import { getCurrentAcademicYear } from '../utils/academicYear';

const AppContext = createContext(null);

// mysql2 returns DECIMAL columns (marks_obtained, max_marks) as strings, not
// numbers — summing them with `+` silently does string concatenation instead
// of addition (e.g. 0 + "80.00" + "99.00" -> "080.0099.00"). Coerce to real
// numbers once here so every consumer (Results, dashboards, reports) gets
// correct totals and clean display (no "80.00", just "80").
const normalizeMarks = (arr) => Array.isArray(arr)
  ? arr.map(m => ({ ...m, marksObtained: Number(m.marksObtained) || 0, maxMarks: Number(m.maxMarks) || 0 }))
  : [];

export function AppProvider({ children }) {
  const toast = useToast();
  const [loading, setLoading]     = useState(true);
  const [apiReady, setApiReady]   = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [academicYear, setAcademicYearState] = useState(getCurrentAcademicYear());
  const [admissionFee, setAdmissionFeeState] = useState(0);
  const academicYearRef = useRef(getCurrentAcademicYear());

  const [students,      setStudents]      = useState([]);
  const [parents,       setParents]       = useState([]);
  const [staff,         setStaff]         = useState([]);
  const [classes,       setClasses]       = useState([]);
  const [subjects,      setSubjects]      = useState([]);
  const [timetable,     setTimetable]     = useState([]);
  const [exams,         setExams]         = useState([]);
  const [marks,         setMarks]         = useState([]);
  const [attendance,    setAttendance]    = useState([]);
  const [feeStructures, setFeeStructures] = useState([]);
  const [feeGroups,     setFeeGroups]     = useState([]);
  const [feePayments,   setFeePayments]   = useState([]);
  const [books,         setBooks]         = useState([]);
  const [bookIssues,    setBookIssues]    = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [homework,      setHomework]      = useState([]);
  const [examTimetable, setExamTimetable] = useState([]);
  const [lessonPlans,   setLessonPlans]   = useState([]);
  const [enquiries,     setEnquiries]     = useState([]);
  const [dropouts,      setDropouts]      = useState([]);
  const [resultPublications, setResultPublications] = useState([]);
  const [hallTicketPrints, setHallTicketPrints] = useState([]);

  const EMPTY_TEACHER_MAPPING = { isClassTeacher: false, classId: '', assignedClassIds: [], assignedSubjectIds: [], classSubjectPairs: [] };
  const [teacherMapping, setTeacherMapping] = useState(EMPTY_TEACHER_MAPPING);

  // Local-only (no backend table)
  const [routes,          setRoutes]          = useLocalStorage(KEYS.ROUTES,           []);
  const [vehicles,        setVehicles]        = useLocalStorage(KEYS.VEHICLES,         []);
  const [notifications,   setNotifications]   = useLocalStorage(KEYS.NOTIFICATIONS,    []);
  const [hwCompletions,   setHwCompletions]   = useLocalStorage(KEYS.HW_COMPLETIONS,   []);
  const [questionPapers,  setQuestionPapers]  = useLocalStorage(KEYS.QUESTION_PAPERS,  []);
  const [transportTerms,  setTransportTermsState]  = useState([]);
  const [schoolName,      setSchoolName]      = useState('');
  const [brandLogo,       setBrandLogo]       = useState('');
  const [brandPrimary,    setBrandPrimary]    = useState('#6b21a8');
  const [brandSecondary,  setBrandSecondary]  = useState('#9333ea');

  useEffect(() => { academicYearRef.current = academicYear; }, [academicYear]);

  // Year-filterable tables — must match backend YEAR_FILTERABLE set
  const YEAR_TABLES = new Set(['exams','fee-structures','fee-payments','attendance','timetable','lesson-plans','marks']);

  const safe = (name) => {
    const yr = academicYearRef.current;
    const url = YEAR_TABLES.has(name) && yr ? `${name}?academicYear=${encodeURIComponent(yr)}` : name;
    return api.list(url).catch(() => []);
  };

  const yr = () => academicYearRef.current;

  const setAcademicYear = useCallback(async (year) => {
    setAcademicYearState(year);
    academicYearRef.current = year;
    // Save to settings.json AND school_settings DB (so backend fee/due logic uses correct year)
    try { await api.create('settings', { academicYear: year }); } catch {}
    try { await api.saveSchoolSettings({ academicYear: year }); } catch {}
    // Reload year-dependent data with new filter
    const reload = async (name, setter) => {
      try {
        const url = `${name}?academicYear=${encodeURIComponent(year)}`;
        setter(await api.list(url));
      } catch {}
    };
    reload('exams',          setExams);
    reload('fee-structures', setFeeStructures);
    reload('fee-payments',   setFeePayments);
    reload('attendance',     setAttendance);
    reload('timetable',      setTimetable);
    reload('lesson-plans',   setLessonPlans);
    reload('marks',          (v) => setMarks(normalizeMarks(v)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setAdmissionFee = useCallback(async (amount) => {
    const val = Number(amount) || 0;
    setAdmissionFeeState(val);
    try { await api.create('settings', { admissionFee: val }); } catch {}
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      // Resolve the real academic year (stored in the DB via school-settings)
      // BEFORE firing any year-filtered fetch below — otherwise those fetches
      // use the initial date-guessed fallback and silently return the wrong
      // (usually empty) slice of data for the rest of this session.
      let schoolSettings = null;
      try {
        schoolSettings = await api.getSchoolSettings();
        if (schoolSettings?.academicYear) {
          setAcademicYearState(schoolSettings.academicYear);
          academicYearRef.current = schoolSettings.academicYear;
        }
      } catch {}
      try {
        const saved = await api.list('settings');
        if (saved?.academicYear) {
          setAcademicYearState(saved.academicYear);
          academicYearRef.current = saved.academicYear;
        }
      } catch {}

      const [s, p, st, cl, sub, tt, ex, mk, att, fs, fp, bk, bi, ann, hw, et, lp, enq, dr, veh, rts, rp, fg, htp] =
        await Promise.all([
          safe('students'),      safe('parents'),        safe('staff'),
          safe('classes'),       safe('subjects'),       safe('timetable'),
          safe('exams'),         safe('marks'),          safe('attendance'),
          safe('fee-structures'),safe('fee-payments'),
          safe('books'),         safe('book-issues'),    safe('announcements'),
          safe('homework'),      safe('exam-timetable'), safe('lesson-plans'),
          safe('enquiries'),     safe('dropouts'),
          safe('transport/vehicles'), safe('transport/routes'),
          safe('result-publications'),
          safe('fee-groups'),
          safe('hall-ticket-prints'),
        ]);
      setStudents(s);       setParents(p);        setStaff(st);
      setClasses(cl);       setSubjects(sub);     setTimetable(tt);
      setExams(ex);         setMarks(normalizeMarks(mk)); setAttendance(att);
      setFeeStructures(fs); setFeePayments(fp);
      setFeeGroups(fg);
      setBooks(bk);         setBookIssues(bi);
      setAnnouncements(ann);setHomework(hw);
      setExamTimetable(et); setLessonPlans(lp);   setEnquiries(enq);
      setDropouts(dr);
      if (Array.isArray(rp)) setResultPublications(rp);
      if (Array.isArray(htp)) setHallTicketPrints(htp);
      if (Array.isArray(veh)) setVehicles(veh);
      if (Array.isArray(rts)) setRoutes(rts);
      // Apply school branding (already fetched above for the academic year)
      if (schoolSettings) {
        const d = schoolSettings;
        if (d?.name) setSchoolName(d.name);
        if (d?.logo) setBrandLogo(d.logo);
        const primary   = d?.primaryColor   || '#6b21a8';
        const secondary = d?.secondaryColor || '#9333ea';
        setBrandPrimary(primary);
        setBrandSecondary(secondary);
        document.documentElement.style.setProperty('--brand-primary',   primary);
        document.documentElement.style.setProperty('--brand-secondary', secondary);
      }
      // Load transport fee terms from DB
      api.getTransportTerms().then(d => { if (Array.isArray(d)) setTransportTermsState(d); }).catch(() => {});
      setApiReady(true);
    } catch (e) {
      setLoadError(e.message);
      setApiReady(false);
    }
    setLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTeacherMapping = useCallback(async () => {
    try {
      const m = await api.getTeacherMapping();
      setTeacherMapping(m);
    } catch { setTeacherMapping(EMPTY_TEACHER_MAPPING); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Saves transport terms to DB and updates local state; returns true/false
  const setTransportTerms = useCallback(async (payload) => {
    try {
      const res = await api.saveTransportTerms(payload);
      setTransportTermsState(res.terms || payload);
      return true;
    } catch { return false; }
  }, []);

  // Reload whenever the auth token changes (e.g. after login)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'sms_auth') loadAll();
    };
    window.addEventListener('storage', onStorage);
    loadAll();
    return () => window.removeEventListener('storage', onStorage);
  }, [loadAll]);

  // ── Direct API save helpers ───────────────────────────────────
  // Each function calls the API directly, then updates local state on success.
  // If the API call fails, state is NOT updated and an error toast is shown.

  const apiCreate = useCallback(async (entity, item, setter) => {
    try {
      await api.create(entity, item);
      setter(prev => [...prev, item]);
      toast?.success('Saved successfully');
      return true;
    } catch (e) {
      toast?.error(`Save failed: ${e.message}`);
      return false;
    }
  }, [toast]);

  const apiUpdate = useCallback(async (entity, id, item, setter) => {
    try {
      await api.update(entity, id, item);
      // Merge, don't replace — `item` is often a PARTIAL update (e.g. a
      // teacher's "syllabus only" edit sends just {examId, syllabus,
      // classSyllabus}, no date/subjectId/classIds/id). Replacing the whole
      // local record with that left the entry missing its own id and other
      // fields the very next render — it looked like the edit "didn't
      // save" even though the server-side partial UPDATE succeeded fine.
      setter(prev => prev.map(x => x.id === id ? { ...x, ...item, id } : x));
      toast?.success('Updated successfully');
      return true;
    } catch (e) {
      toast?.error(`Update failed: ${e.message}`);
      return false;
    }
  }, [toast]);

  const apiDelete = useCallback(async (entity, id, setter) => {
    try {
      await api.remove(entity, id);
      setter(prev => prev.filter(x => x.id !== id));
      toast?.success('Deleted');
      return true;
    } catch (e) {
      toast?.error(`Delete failed: ${e.message}`);
      return false;
    }
  }, [toast]);

  // ── Entity-specific save/update/delete ────────────────────────
  const saveClass     = (item)    => apiCreate('classes',        item, setClasses);
  const updateClass   = (id, item)=> apiUpdate('classes',        id, item, setClasses);
  const deleteClass   = (id)      => apiDelete('classes',        id, setClasses);

  const saveStudent   = (item)    => apiCreate('students',       item, setStudents);
  const updateStudent = (id, item)=> apiUpdate('students',       id, item, setStudents);
  const deleteStudent = (id)      => apiDelete('students',       id, setStudents);

  const saveStaff     = (item)    => apiCreate('staff',          item, setStaff);
  const updateStaff   = (id, item)=> apiUpdate('staff',          id, item, setStaff);
  const deleteStaff   = (id)      => apiDelete('staff',          id, setStaff);

  const saveSubject   = (item)    => apiCreate('subjects',       item, setSubjects);
  const updateSubject = (id, item)=> apiUpdate('subjects',       id, item, setSubjects);
  const deleteSubject = (id)      => apiDelete('subjects',       id, setSubjects);
  const saveFeeGroup   = (item)    => apiCreate('fee-groups',     item, setFeeGroups);
  const updateFeeGroup = (id, item)=> apiUpdate('fee-groups',     id, item, setFeeGroups);
  const deleteFeeGroup = (id)      => apiDelete('fee-groups',     id, setFeeGroups);

  const saveParent    = (item)    => apiCreate('parents',        item, setParents);
  const updateParent  = (id, item)=> apiUpdate('parents',        id, item, setParents);

  const saveExam      = (item)    => apiCreate('exams',          item, setExams);
  const updateExam    = (id, item)=> apiUpdate('exams',          id, item, setExams);
  const deleteExam    = (id)      => apiDelete('exams',          id, setExams);

  // Bespoke create (not the generic apiCreate) — announcements must show
  // newest-first everywhere they're read (Announcements page, every
  // dashboard's preview list), but apiCreate appends to the end of local
  // state. Prepending here fixes every consumer at once instead of each
  // screen needing its own re-sort.
  const saveAnnouncement = useCallback(async (item) => {
    try {
      await api.create('announcements', item);
      setAnnouncements(prev => [item, ...prev]);
      toast?.success('Saved successfully');
      return true;
    } catch (e) {
      toast?.error(`Save failed: ${e.message}`);
      return false;
    }
  }, [toast]);
  const updateAnnouncement = (id, item)=> apiUpdate('announcements',  id, item, setAnnouncements);
  const deleteAnnouncement = (id)      => apiDelete('announcements',  id, setAnnouncements);

  const saveBook      = (item)    => apiCreate('books',          item, setBooks);
  const updateBook    = (id, item)=> apiUpdate('books',          id, item, setBooks);
  const deleteBook    = (id)      => apiDelete('books',          id, setBooks);

  const saveBookIssue = (item)    => apiCreate('book-issues',    item, setBookIssues);
  const updateBookIssue=(id,item) => apiUpdate('book-issues',    id, item, setBookIssues);

  const saveFeeStructure   = (item)    => apiCreate('fee-structures', item, setFeeStructures);
  const updateFeeStructure = (id, item)=> apiUpdate('fee-structures', id, item, setFeeStructures);
  const deleteFeeStructure = (id)      => apiDelete('fee-structures', id, setFeeStructures);

  // Routes to a dedicated endpoint per fee type — the server hardcodes the
  // type there, so no frontend bug (wrong button, stale state, etc.) can
  // ever save a payment under the wrong category again.
  const FEE_PAYMENT_ENDPOINT = {
    transportFee: 'fee-payments/transport',
    admissionFee: 'fee-payments/admission',
    current:      'fee-payments/tuition',
  };
  const saveFeePayment     = (item)    => apiCreate(FEE_PAYMENT_ENDPOINT[item.type] || 'fee-payments', item, setFeePayments);
  const updateFeePayment   = (id, item)=> apiUpdate('fee-payments',   id, item, setFeePayments);
  const deleteFeePayment   = (id)      => apiDelete('fee-payments',   id, setFeePayments);

  const saveAttendance     = (item)    => apiCreate('attendance',     item, setAttendance);
  const updateAttendance   = (id, item)=> apiUpdate('attendance',     id, item, setAttendance);
  // Re-fetches attendance from the server — the web app has no live sync, so
  // this is how screens like AdminDashboard pick up attendance a teacher
  // just marked from the mobile app without the admin having to reload the
  // whole page.
  const refreshAttendance  = useCallback(async () => { setAttendance(await safe('attendance')); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveHomework       = (item)    => apiCreate('homework',       item, setHomework);
  const updateHomework     = (id, item)=> apiUpdate('homework',       id, item, setHomework);
  const deleteHomework     = (id)      => apiDelete('homework',       id, setHomework);

  const saveTimetable      = (item)    => apiCreate('timetable',      item, setTimetable);
  const updateTimetable    = (id, item)=> apiUpdate('timetable',      id, item, setTimetable);
  const deleteTimetable    = (id)      => apiDelete('timetable',      id, setTimetable);

  // Bespoke create (not the generic apiCreate) — an exam+subject pair must
  // resolve to exactly one max-marks value everywhere (marks entry, results,
  // report cards all key off examId+subjectId with no classId in the
  // lookup). If a schedule entry for this exam+subject already exists
  // locally, the backend merges the POST into that existing row instead of
  // inserting a duplicate; mirror that here so the UI reflects the merge
  // immediately instead of showing a stale duplicate until the next reload.
  const saveExamTimetable  = async (item) => {
    const existing = examTimetable.find(e => e.examId === item.examId && e.subjectId === item.subjectId);
    if (existing) {
      const merged = { ...item, id: existing.id };
      try {
        await api.update('exam-timetable', existing.id, merged);
        setExamTimetable(prev => prev.map(x => x.id === existing.id ? merged : x));
        toast?.success('Saved successfully');
        return true;
      } catch (e) {
        toast?.error(`Save failed: ${e.message}`);
        return false;
      }
    }
    return apiCreate('exam-timetable', item, setExamTimetable);
  };
  const updateExamTimetable= (id, item)=> apiUpdate('exam-timetable', id, item, setExamTimetable);
  const deleteExamTimetable= (id)      => apiDelete('exam-timetable', id, setExamTimetable);

  const saveMarks          = (item)    => apiCreate('marks',          item, setMarks);
  const updateMarks        = (id, item)=> apiUpdate('marks',          id, item, setMarks);

  const isResultPublished = (examId, studentId) =>
    resultPublications.some(r => r.examId === examId && r.studentId === studentId);
  const publishResults = async (examId, studentIds) => {
    try {
      await api.post('/result-publications', { examId, studentIds });
      setResultPublications(prev => [...prev, ...studentIds.map(studentId => ({ examId, studentId }))]);
      toast?.success(`Results published for ${studentIds.length} student${studentIds.length === 1 ? '' : 's'}`);
      return true;
    } catch (e) { toast?.error(`Publish failed: ${e.message}`); return false; }
  };
  const unpublishResults = async (examId, studentIds) => {
    try {
      await api.del('/result-publications', { examId, studentIds });
      setResultPublications(prev => prev.filter(r => !(r.examId === examId && studentIds.includes(r.studentId))));
      toast?.success('Results unpublished');
      return true;
    } catch (e) { toast?.error(`Unpublish failed: ${e.message}`); return false; }
  };

  const isHallTicketPrinted = (examId, studentId) =>
    hallTicketPrints.some(r => r.examId === examId && r.studentId === studentId);
  const markHallTicketsPrinted = async (examId, studentIds) => {
    try {
      await api.post('/hall-ticket-prints', { examId, studentIds });
      const now = new Date().toISOString();
      setHallTicketPrints(prev => [
        ...prev.filter(r => !(r.examId === examId && studentIds.includes(r.studentId))),
        ...studentIds.map(studentId => ({ examId, studentId, printedAt: now })),
      ]);
      return true;
    } catch (e) { toast?.error(`Couldn't mark hall ticket as printed: ${e.message}`); return false; }
  };

  const saveLessonPlan     = (item)    => apiCreate('lesson-plans',   item, setLessonPlans);
  const updateLessonPlan   = (id, item)=> apiUpdate('lesson-plans',   id, item, setLessonPlans);
  const deleteLessonPlan   = (id)      => apiDelete('lesson-plans',   id, setLessonPlans);

  const saveEnquiry        = (item)    => apiCreate('enquiries',      item, setEnquiries);
  const updateEnquiry      = (id, item)=> apiUpdate('enquiries',      id, item, setEnquiries);
  const deleteEnquiry      = (id)      => apiDelete('enquiries',      id, setEnquiries);

  const saveDropout        = (item)    => apiCreate('dropouts',       item, setDropouts);
  const deleteDropout      = (id)      => apiDelete('dropouts',       id, setDropouts);

  const saveVehicle        = (item)    => apiCreate('transport/vehicles', item, setVehicles);
  const updateVehicle      = (id, item)=> apiUpdate('transport/vehicles', id, item, setVehicles);
  const deleteVehicle      = (id)      => apiDelete('transport/vehicles', id, setVehicles);

  const saveRoute          = (item)    => apiCreate('transport/routes',   item, setRoutes);
  const updateRoute        = (id, item)=> apiUpdate('transport/routes',   id, item, setRoutes);
  const deleteRoute        = (id)      => apiDelete('transport/routes',   id, setRoutes);

  return (
    <AppContext.Provider value={{
      loading, apiReady, loadError, reloadData: loadAll,
      academicYear, setAcademicYear,
      admissionFee, setAdmissionFee,
      teacherMapping, loadTeacherMapping,

      students,      setStudents,
      parents,       setParents,
      staff,         setStaff,
      classes,       setClasses,
      subjects,      setSubjects,
      timetable,     setTimetable,
      exams,         setExams,
      marks,         setMarks,
      attendance,    setAttendance,
      feeStructures, setFeeStructures,
      feePayments,   setFeePayments,
      books,         setBooks,
      bookIssues,    setBookIssues,
      announcements, setAnnouncements,
      homework,      setHomework,
      examTimetable, setExamTimetable,
      lessonPlans,   setLessonPlans,
      enquiries,     setEnquiries,

      routes,         setRoutes,
      vehicles,       setVehicles,
      notifications,  setNotifications,
      hwCompletions,  setHwCompletions,
      questionPapers, setQuestionPapers,
      transportTerms, setTransportTerms, setTransportTermsState,
      schoolName,     setSchoolName,
      brandLogo,      setBrandLogo,
      brandPrimary,   setBrandPrimary,
      brandSecondary, setBrandSecondary,

      // Direct API save/update/delete (use these in forms)
      saveClass,     updateClass,     deleteClass,
      saveStudent,   updateStudent,   deleteStudent,
      saveStaff,     updateStaff,     deleteStaff,
      saveSubject,   updateSubject,   deleteSubject,
      feeGroups,     saveFeeGroup,    updateFeeGroup,  deleteFeeGroup,
      saveParent,    updateParent,
      saveExam,      updateExam,      deleteExam,
      saveAnnouncement, updateAnnouncement, deleteAnnouncement,
      saveBook,      updateBook,      deleteBook,
      saveBookIssue, updateBookIssue,
      saveFeeStructure, updateFeeStructure, deleteFeeStructure,
      saveFeePayment,   updateFeePayment,  deleteFeePayment,
      saveAttendance,   updateAttendance,   refreshAttendance,
      saveHomework,  updateHomework,  deleteHomework,
      saveTimetable, updateTimetable, deleteTimetable,
      saveExamTimetable, updateExamTimetable, deleteExamTimetable,
      saveMarks,     updateMarks,
      resultPublications, isResultPublished, publishResults, unpublishResults,
      hallTicketPrints, isHallTicketPrinted, markHallTicketsPrinted,
      saveLessonPlan, updateLessonPlan, deleteLessonPlan,
      saveEnquiry,   updateEnquiry,   deleteEnquiry,
      dropouts,      setDropouts,     saveDropout,     deleteDropout,
      saveVehicle,   updateVehicle,   deleteVehicle,
      saveRoute,     updateRoute,     deleteRoute,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  return useContext(AppContext);
}
